import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider } from '@contexts/AuthContext';
import { MarketProvider } from '@contexts/MarketContext';
import { NotificationProvider } from '@contexts/NotificationContext';
import { ErrorBoundary } from '@components/ErrorBoundary';
import App from './App';
import './index.css';

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

/**
 * Check if mock auth mode is enabled
 */
const isMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

/**
 * Auth0 configuration from environment variables
 */
const auth0Config = {
  domain: import.meta.env.VITE_AUTH0_DOMAIN || '',
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
  audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
};

/**
 * Validate Auth0 configuration when not in mock mode
 */
function validateAuth0Config(): boolean {
  if (isMockAuth) return true;

  const { domain, clientId, audience } = auth0Config;

  if (!domain || !clientId || !audience) {
    console.error(
      'Auth0 configuration is incomplete. Required environment variables:',
      '\n  - VITE_AUTH0_DOMAIN',
      '\n  - VITE_AUTH0_CLIENT_ID',
      '\n  - VITE_AUTH0_AUDIENCE',
      '\n\nSet VITE_USE_MOCK_AUTH=true to use mock authentication.'
    );
    return false;
  }

  return true;
}

/**
 * App wrapper component that conditionally includes Auth0Provider
 */
function AppWrapper(): JSX.Element {
  // In mock mode, don't wrap with Auth0Provider
  if (isMockAuth) {
    return (
      <BrowserRouter>
        <MarketProvider>
          <NotificationProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </NotificationProvider>
        </MarketProvider>
      </BrowserRouter>
    );
  }

  // Validate Auth0 config
  if (!validateAuth0Config()) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-red-50">
        <div className="rounded-lg bg-white p-8 shadow-lg">
          <h1 className="mb-4 text-2xl font-bold text-red-600">Configuration Error</h1>
          <p className="text-gray-700">
            Auth0 configuration is incomplete. Please check the console for details.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            Set <code className="rounded bg-gray-100 px-2 py-1">VITE_USE_MOCK_AUTH=true</code> for
            development without Auth0.
          </p>
        </div>
      </div>
    );
  }

  // Auth0 mode - wrap with Auth0Provider
  return (
    <BrowserRouter>
      <MarketProvider>
        <NotificationProvider>
          <Auth0Provider
            domain={auth0Config.domain}
            clientId={auth0Config.clientId}
            authorizationParams={{
              redirect_uri: window.location.origin,
              audience: auth0Config.audience,
              scope: 'openid profile email offline_access',
            }}
            useRefreshTokens={true}
            useRefreshTokensFallback={true}
            cacheLocation="localstorage"
          >
            <AuthProvider>
              <App />
            </AuthProvider>
          </Auth0Provider>
        </NotificationProvider>
      </MarketProvider>
    </BrowserRouter>
  );
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppWrapper />
    </ErrorBoundary>
  </React.StrictMode>
);
